import { useState } from 'react';
import SectionHeading from './shared/SectionHeading.jsx';
import SectionReveal from './shared/SectionReveal.jsx';
import Toast from './shared/Toast.jsx';
import { services } from '../data/services.js';

const SERVICE_OPTIONS = services.map((s) => ({ id: s.id, label: s.title }));

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[+]?[\d\s()-]{6,20}$/;

function Field({ label, name, type = 'text', value, onChange, error, required, placeholder, textarea }) {
  const shared = {
    id: name,
    name,
    value,
    onChange,
    placeholder,
    'aria-invalid': !!error,
    'aria-describedby': error ? `${name}-error` : undefined,
    className: `w-full rounded-xl border bg-navy-800/40 px-4 py-3 text-white placeholder-silver/40 outline-none transition-all duration-300 focus:bg-navy-800/70 ${
      error
        ? 'border-red-400/60 focus:border-red-400'
        : 'border-white/10 focus:border-cyan/60 focus:shadow-glow'
    }`,
  };

  return (
    <div>
      <label htmlFor={name} className="mb-2 block font-heading text-sm font-medium text-silver-light">
        {label} {required && <span className="text-cyan">*</span>}
      </label>
      {textarea ? (
        <textarea rows={4} {...shared} />
      ) : (
        <input type={type} {...shared} />
      )}
      {error && (
        <p id={`${name}-error`} className="mt-1.5 text-xs text-red-300">
          {error}
        </p>
      )}
    </div>
  );
}

export default function ContactForm() {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    services: [],
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState({ open: false, variant: 'success', title: '', message: '' });

  const update = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const toggleService = (id) => {
    setForm((f) => ({
      ...f,
      services: f.services.includes(id)
        ? f.services.filter((s) => s !== id)
        : [...f.services, id],
    }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Please tell us your name.';
    if (!form.email.trim()) e.email = 'Email is required.';
    else if (!EMAIL_RE.test(form.email)) e.email = 'That email looks off.';
    if (form.phone && !PHONE_RE.test(form.phone)) e.phone = 'Phone number looks off.';
    if (!form.message.trim()) e.message = 'Tell us a little about your project.';
    if (form.services.length === 0) e.services = 'Pick at least one service.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);

    const payload = {
      name: form.name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim() || null,
      message: form.message.trim(),
      services: form.services,
    };

    try {
      const baseUrl = import.meta.env.VITE_API_URL || '';
      const res = await fetch(`${baseUrl}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error || `Request failed (${res.status})`);
      }

      setToast({
        open: true,
        variant: 'success',
        title: 'Message received!',
        message: 'Thanks — we reply to every inquiry within one business day.',
      });
      setForm({ name: '', email: '', phone: '', message: '', services: [] });
      setErrors({});
    } catch (err) {
      setToast({
        open: true,
        variant: 'error',
        title: 'Something went wrong',
        message: `${err.message} — please email us directly at hello@thaitsolution.co`,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="section-pad relative overflow-hidden">
      <div aria-hidden className="pointer-events-none absolute left-1/4 top-0 h-80 w-80 rounded-full bg-cyan/10 blur-[130px]" />

      <div className="container-x">
        <SectionHeading
          eyebrow="Start a conversation"
          title="Tell us about your project."
          subtitle="No sales calls. A senior engineer will read your note and reply with concrete next steps — usually within a business day."
        />

        <div className="mt-16 grid gap-8 lg:grid-cols-[1fr_1.4fr]">
          {/* Left: contact info */}
          <SectionReveal className="flex flex-col gap-6">
            <div className="card-surface p-8">
              <h3 className="font-heading text-xl font-bold text-white">Reach us directly</h3>
              <ul className="mt-6 space-y-5">
                {[
                  { label: 'Email', value: 'hello@thaitsolution.co', href: 'mailto:hello@thaitsolution.co', icon: 'mail' },
                  { label: 'Phone', value: '+66 2 123 4567', href: 'tel:+6621234567', icon: 'phone' },
                  { label: 'Office', value: 'Sathorn, Bangkok, Thailand', href: null, icon: 'pin' },
                ].map((item) => (
                  <li key={item.label} className="flex items-start gap-4">
                    <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-cyan/15 text-cyan">
                      {item.icon === 'mail' && (
                        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
                          <rect x="3" y="5" width="18" height="14" rx="2" />
                          <path d="m3 7 9 6 9-6" />
                        </svg>
                      )}
                      {item.icon === 'phone' && (
                        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
                          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
                        </svg>
                      )}
                      {item.icon === 'pin' && (
                        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
                          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                          <circle cx="12" cy="10" r="3" />
                        </svg>
                      )}
                    </span>
                    <div>
                      <div className="text-xs uppercase tracking-wider text-silver/60">{item.label}</div>
                      {item.href ? (
                        <a href={item.href} className="font-heading text-sm font-medium text-white transition-colors hover:text-cyan">
                          {item.value}
                        </a>
                      ) : (
                        <div className="font-heading text-sm font-medium text-white">{item.value}</div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="card-surface p-8">
              <h3 className="font-heading text-base font-semibold text-white">Engagement models</h3>
              <ul className="mt-4 space-y-3 text-sm text-silver/75">
                <li className="flex gap-3"><span className="text-cyan">·</span> Fixed-scope projects with milestone billing</li>
                <li className="flex gap-3"><span className="text-cyan">·</span> Dedicated squads on monthly retainer</li>
                <li className="flex gap-3"><span className="text-cyan">·</span> Fractional advisory & code reviews</li>
              </ul>
            </div>
          </SectionReveal>

          {/* Right: form */}
          <SectionReveal delay={120}>
            <form onSubmit={handleSubmit} noValidate className="card-surface p-8 sm:p-10">
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Full name" name="name" value={form.name} onChange={update} required placeholder="Jane Phromma" error={errors.name} />
                <Field label="Email" name="email" type="email" value={form.email} onChange={update} required placeholder="jane@company.com" error={errors.email} />
              </div>

              <div className="mt-5">
                <Field label="Phone (optional)" name="phone" type="tel" value={form.phone} onChange={update} placeholder="+66 8 1234 5678" error={errors.phone} />
              </div>

              {/* Service pills */}
              <div className="mt-5">
                <span className="mb-2 block font-heading text-sm font-medium text-silver-light">
                  What do you need? <span className="text-cyan">*</span>
                </span>
                <div className="flex flex-wrap gap-2">
                  {SERVICE_OPTIONS.map((s) => {
                    const selected = form.services.includes(s.id);
                    return (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => toggleService(s.id)}
                        aria-pressed={selected}
                        className={`rounded-full border px-4 py-2 font-heading text-sm font-medium transition-all duration-300 ${
                          selected
                            ? 'border-cyan bg-cyan-sheen text-navy-900 shadow-glow'
                            : 'border-white/15 text-silver-light hover:border-cyan/50 hover:text-white'
                        }`}
                      >
                        {s.label}
                      </button>
                    );
                  })}
                </div>
                {errors.services && <p className="mt-1.5 text-xs text-red-300">{errors.services}</p>}
              </div>

              <div className="mt-5">
                <Field
                  label="Project details"
                  name="message"
                  value={form.message}
                  onChange={update}
                  required
                  placeholder="What are you building, what's the timeline, and what does success look like?"
                  textarea
                  error={errors.message}
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="btn-primary animate-pulse-glow mt-7 w-full disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting ? (
                  <>
                    <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.25" />
                      <path d="M22 12a10 10 0 0 0-10-10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                    </svg>
                    Sending...
                  </>
                ) : (
                  <>
                    Start Your Project
                    <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none">
                      <path d="M4 10h12m0 0l-5-5m5 5l-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </>
                )}
              </button>

              <p className="mt-4 text-center text-xs text-silver/50">
                By submitting, you agree to our privacy policy. We never share your information.
              </p>
            </form>
          </SectionReveal>
        </div>
      </div>

      <Toast
        open={toast.open}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        title={toast.title}
        message={toast.message}
        variant={toast.variant}
      />
    </section>
  );
}
